/**
 * Caminho interno seguro pós-login (evita open redirect).
 */
export function safeInternalPath(raw: string | null | undefined): string | null {
  const s = raw?.trim();
  if (!s || !s.startsWith("/") || s.startsWith("//") || s.includes("://")) return null;
  if (s.length > 2048) return null;
  return s;
}

/**
 * Corpo JSON "flat" do Backmanwha em `POST auth/login`, `auth/login/google`, `auth/login/discord`
 * → objeto compatível com o que o site espera de `usuarios/me` (usr_*).
 */
export function userDataFromAuthLoginBody(body: Record<string, unknown>): Record<string, unknown> | undefined {
  const nested = body.usuario ?? body.user;
  if (nested && typeof nested === "object") return nested as Record<string, unknown>;
  const idRaw = body.usr_id ?? body.id;
  const usr_id = typeof idRaw === "number" ? idRaw : Number(idRaw);
  if (!Number.isFinite(usr_id) || usr_id <= 0) return undefined;
  return {
    usr_id,
    usr_nome: body.usr_nome,
    usr_nick: body.usr_nick ?? "",
    usr_imagem: body.usr_imagem,
    usr_banner: body.usr_banner,
    vip_acaba_em: body.vip_acaba_em ?? body.vip_acabaem,
  };
}

/** Estado OAuth Discord: `next` após login (só web). */
export function buildDiscordOAuthState(nextParam: string | null | undefined): string | undefined {
  const next = safeInternalPath(nextParam ?? undefined);
  if (!next) return undefined;
  const b64 = btoa(JSON.stringify({ next }))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  return b64;
}

export type DiscordReturnState =
  | { mode: "app" }
  | { mode: "web"; next?: string };

/** Interpreta `state` devolvido pelo Discord (app deep link vs web + next). */
export function parseDiscordReturnState(state: string | null): DiscordReturnState {
  if (!state?.trim()) return { mode: "web" };
  const s = state.trim();
  const lower = s.toLowerCase();
  if (lower === "from_app" || lower.startsWith("from_app")) {
    return { mode: "app" };
  }
  try {
    const padLen = (4 - (s.length % 4)) % 4;
    const padded = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLen);
    const json = JSON.parse(atob(padded)) as { next?: unknown; app?: unknown };
    if (json.app === true) {
      return { mode: "app" };
    }
    const next = safeInternalPath(typeof json.next === "string" ? json.next : undefined);
    return { mode: "web", next: next ?? undefined };
  } catch {
    if (lower.includes("from_app")) return { mode: "app" };
    return { mode: "web" };
  }
}
